// The bento canvas. Renders one Node per Agent, draws bezier edges
// from manager_id, supports pan (drag the empty viewport) + zoom
// (wheel + zoom buttons) + per-node drag (with position persistence
// on drop), and click-to-open the agent modal.

import { useEffect, useRef, useState } from "react"
import { useStore } from "../../stores/store"
import {
  canvasStore, loadAgents, updateAgentPosition, persistAgentPosition,
  setView, zoomBy, resetView, focusAgent,
} from "../../stores/canvasStore"
import { loadApprovals } from "../../stores/approvalsStore"
import { sectionsStore, PERSONAL_SECTION_ID } from "../../stores/sectionsStore"
import { useBootstrap, useTranslations } from "../../lib/translations"
import { Node } from "./Node"
import { Legend } from "./Legend"
import { AddZone } from "./AddZone"

const NODE_WIDTH = 240
const NODE_HEIGHT_GUESS = 170 // good enough for bezier mid-y

export function Canvas() {
  const t = useTranslations()
  const bootstrap = useBootstrap()
  const allAgents = useStore(canvasStore, (s) => s.agents)
  const loading   = useStore(canvasStore, (s) => s.loading)
  const view      = useStore(canvasStore, (s) => s.view)
  const search    = useStore(canvasStore, (s) => s.search)
  const focused   = useStore(canvasStore, (s) => s.focusedAgentId)
  const currentSectionId = useStore(sectionsStore, (s) => s.currentSectionId)

  // Filter agents by current section selection. Personal = agents
  // whose owner_user_id matches the current user. Named section =
  // agents with that section_id. Section filter is applied BEFORE the
  // search dim — keeps the canvas focused on what the user picked.
  const agents = filterBySection(allAgents, currentSectionId, bootstrap.user?.id)

  const viewportRef = useRef(null)
  const worldRef    = useRef(null)
  const tipRef      = useRef(null)
  const [tip, setTip] = useState(null)
  const drag = useRef(null) // either { mode: "pan", ... } or { mode: "node", id, ... }
  const wasMoved = useRef(false) // set on pointerup if a drag actually moved — suppresses the click that fires after

  // initial load: agents + pending sign-offs.
  useEffect(() => {
    loadAgents(bootstrap.paths)
    loadApprovals(bootstrap.paths)
  }, [bootstrap.paths])

  // pan + zoom on the viewport
  useEffect(() => {
    const viewport = viewportRef.current
    if (!viewport) return

    const onWheel = (e) => {
      e.preventDefault()
      zoomBy(e.deltaY < 0 ? 1.08 : 0.93)
    }
    viewport.addEventListener("wheel", onWheel, { passive: false })
    return () => viewport.removeEventListener("wheel", onWheel)
  }, [])

  const onViewportPointerDown = (e) => {
    if (e.target.closest(".cm-node")) return
    drag.current = { mode: "pan", startX: e.clientX, startY: e.clientY, tx: view.tx, ty: view.ty }
    viewportRef.current?.classList.add("panning")
  }

  const onNodePointerDown = (agent) => (e) => {
    e.stopPropagation()
    drag.current = {
      mode: "node",
      id: agent.id,
      startX: e.clientX,
      startY: e.clientY,
      ox: agent.x, oy: agent.y,
      moved: false,
    }
  }

  useEffect(() => {
    const onMove = (e) => {
      const d = drag.current
      if (!d) return
      if (d.mode === "pan") {
        setView({ ...view, tx: d.tx + (e.clientX - d.startX), ty: d.ty + (e.clientY - d.startY) })
      } else {
        const dx = (e.clientX - d.startX) / view.scale
        const dy = (e.clientY - d.startY) / view.scale
        if (Math.abs(dx) + Math.abs(dy) > 3) d.moved = true
        updateAgentPosition(d.id, d.ox + dx, d.oy + dy)
      }
    }
    const onUp = (e) => {
      const d = drag.current
      drag.current = null
      viewportRef.current?.classList.remove("panning")
      if (d && d.mode === "node") {
        wasMoved.current = d.moved
        if (d.moved) {
          const agent = canvasStore.getState().agents.find((a) => a.id === d.id)
          if (agent) persistAgentPosition(agent.id, agent.x, agent.y, bootstrap.paths).catch(() => {})
        }
      }
    }
    window.addEventListener("pointermove", onMove)
    window.addEventListener("pointerup", onUp)
    return () => {
      window.removeEventListener("pointermove", onMove)
      window.removeEventListener("pointerup", onUp)
    }
  }, [view, bootstrap.paths])

  const onNodeClick = (agent) => (e) => {
    // suppress click-after-drag: pointerup runs before click and sets wasMoved
    if (wasMoved.current) {
      wasMoved.current = false
      return
    }
    focusAgent(agent.id)
  }

  const onNodeEnter = (agent) => (e) => {
    setTip({ agent, x: e.clientX, y: e.clientY })
  }
  const onNodeLeave = () => setTip(null)

  // dim non-matching nodes when searching
  const q = search.trim().toLowerCase()
  const isDim = (a) => q && !`${a.name} ${a.role} ${a.status}`.toLowerCase().includes(q)

  const transform = `translate(${view.tx}px,${view.ty}px) scale(${view.scale})`

  return (
    <div className="cm-stage">
      <Legend />
      <div className="cm-viewport" ref={viewportRef} onPointerDown={onViewportPointerDown}>
        <div className="cm-world" ref={worldRef} style={{ transform }}>
          <Edges agents={agents} />
          {agents.map((a) => (
            <Node key={a.id}
                  agent={a}
                  dimmed={isDim(a)}
                  focused={focused === a.id}
                  onPointerDown={onNodePointerDown(a)}
                  onClick={onNodeClick(a)}
                  onMouseEnter={onNodeEnter(a)}
                  onMouseLeave={onNodeLeave} />
          ))}
        </div>
      </div>
      <div className="cm-zoom">
        <button onClick={() => zoomBy(1.15)} title={t("canvas.zoom.in")}>+</button>
        <button onClick={() => zoomBy(0.87)} title={t("canvas.zoom.out")}>−</button>
        <button onClick={() => resetView()} title={t("canvas.zoom.reset")} style={{ fontSize: 14 }}>⤢</button>
      </div>
      <AddZone />

      {agents.length === 0 && !loading && (
        <div className="cm-empty">
          <div>
            <b>{t("canvas.empty.headline")}</b>
            <div>{t("canvas.empty.sub")}</div>
          </div>
        </div>
      )}

      {tip && (
        <div ref={tipRef} className="cm-tip show"
             style={{ left: tip.x + 16, top: tip.y + 16 }}>
          <b>{tip.agent.name}</b>
          {tip.agent.master ? t("canvas.tip.master") : t("canvas.tip.no_manager")}
          <span className="mono"> · {t("canvas.tip.canary_label")} pass</span>
        </div>
      )}
    </div>
  )
}

function filterBySection(agents, sectionId, userId) {
  if (sectionId === PERSONAL_SECTION_ID) {
    return agents.filter((a) => a.owner_user_id && a.owner_user_id === userId)
  }
  return agents.filter((a) => a.section_id === sectionId)
}

function Edges({ agents }) {
  const byId = Object.fromEntries(agents.map((a) => [a.id, a]))
  const edges = agents
    .filter((a) => a.manager_id && byId[a.manager_id])
    .map((a) => {
      const p = byId[a.manager_id]
      const c1 = { x: p.x + NODE_WIDTH / 2, y: p.y + NODE_HEIGHT_GUESS / 2 }
      const c2 = { x: a.x + NODE_WIDTH / 2, y: a.y + NODE_HEIGHT_GUESS / 2 }
      const midY = (c1.y + c2.y) / 2
      return {
        key: `${p.id}->${a.id}`,
        d: `M${c1.x},${c1.y} C${c1.x},${midY} ${c2.x},${midY} ${c2.x},${c2.y}`,
      }
    })

  return (
    <svg className="cm-edges">
      {edges.map((e) => <path key={e.key} className="cm-edge" d={e.d} />)}
    </svg>
  )
}
